import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Pagination = ({ currentPage, totalPages, onPageChange, isDarkMode }) => {
  const pages = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pageNumbers = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) {
      pageNumbers.push('ellipsis-left');
    }

    for (let i = start; i <= end; i += 1) {
      pageNumbers.push(i);
    }

    if (end < totalPages - 1) {
      pageNumbers.push('ellipsis-right');
    }

    pageNumbers.push(totalPages);
    return pageNumbers;
  }, [currentPage, totalPages]);

  if (totalPages <= 1) return null;

  return (
    <div className={`mt-8 flex flex-col md:flex-row items-center justify-between gap-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 md:p-6 shadow-sm`}>
      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        Page {currentPage} of {totalPages}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium border transition-all ${currentPage === 1
            ? isDarkMode
              ? 'bg-gray-700 text-gray-500 border-gray-700 cursor-not-allowed'
              : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            : isDarkMode
              ? 'bg-gray-900 text-gray-200 border-gray-700 hover:bg-gray-700'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </button>

        <div className="flex items-center gap-1">
          {pages.map((page) => {
            if (page === 'ellipsis-left' || page === 'ellipsis-right') {
              return (
                <span
                  key={page}
                  className={`px-2 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
                >
                  ...
                </span>
              );
            }

            const isActive = page === currentPage;
            return (
              <button
                key={page}
                type="button"
                onClick={() => onPageChange(page)}
                className={`w-9 h-9 rounded-lg text-sm font-semibold transition-all ${isActive
                  ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                  : isDarkMode
                    ? 'bg-gray-900 text-gray-200 hover:bg-gray-700'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
              >
                {page}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium border transition-all ${currentPage === totalPages
            ? isDarkMode
              ? 'bg-gray-700 text-gray-500 border-gray-700 cursor-not-allowed'
              : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            : isDarkMode
              ? 'bg-gray-900 text-gray-200 border-gray-700 hover:bg-gray-700'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
